import { createSelector } from 'reselect';

import type { RootState } from 'store/configureStore';

import { types, type PartFileCadDataAction } from './actions';
import type { PartFileCadEntities } from './types';

type PartFileCadData = {
  entities: PartFileCadEntities;
  isError: boolean;
  isLoading: boolean;
  rootViewerBodyViewerId: string | null;
};

const partFileCadDataInitialState: PartFileCadData = {
  entities: {
    partFiles: {},
    viewerBodies: {},
    viewerParts: {},
  },
  isError: false,
  isLoading: false,
  rootViewerBodyViewerId: null,
};

type PartFileCadDataState = {
  cadData: Record<string, PartFileCadData>;
};

const initialState: PartFileCadDataState = {
  cadData: {},
};

const reducer = (
  state: PartFileCadDataState = initialState,
  action: PartFileCadDataAction,
): PartFileCadDataState => {
  switch (action.type) {
    case types.FETCH_PART_FILE_CAD_DATA_ERROR: {
      const { payload } = action;
      if (payload.partFileUuid) {
        return {
          ...state,
          cadData: {
            ...state.cadData,
            [payload.partFileUuid]: {
              ...state.cadData[payload.partFileUuid],
              isLoading: false,
              isError: true,
            },
          },
        };
      } else {
        return {
          ...state,
        };
      }
    }
    case types.FETCH_PART_FILE_CAD_DATA_INIT: {
      const { payload } = action;
      return {
        ...state,
        cadData: {
          ...state.cadData,
          [payload.partFileUuid]: {
            ...(state.cadData[payload.partFileUuid] ||
              partFileCadDataInitialState),
            isError: false,
            isLoading: true,
          },
        },
      };
    }
    case types.FETCH_PART_FILE_CAD_DATA_SUCCESS: {
      const { payload } = action;
      return {
        ...state,
        cadData: {
          ...state.cadData,
          [payload.partFileUuid]: {
            ...state.cadData[payload.partFileUuid],
            entities: payload.entities,
            isLoading: false,
            rootViewerBodyViewerId: payload.rootViewerBodyViewerId,
          },
        },
      };
    }
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): PartFileCadDataState =>
  state.modules.common.viewer.cadViewer.partFileCadData;

export const getCadData = (state: RootState) => getScope(state).cadData;

export const getIsAnyPartFileCadDataFetching = (state: RootState) => {
  const cadData = getCadData(state) ?? {};
  return Object.values(cadData).some((part) => part?.isLoading);
};

export const getCadDataForPartFile = (state: RootState, partFileUuid: string) =>
  getCadData(state)?.[partFileUuid] ?? null;

export const getPartFileCadDataExists = (
  state: RootState,
  partFileUuid: string,
) => {
  const cadData = getCadData(state)?.[partFileUuid] ?? null;
  return !!cadData && !cadData?.isError;
};

export const getAllViewerParts = createSelector(getCadData, (cadData) =>
  Object.values(cadData).flatMap((part) =>
    Object.values(part?.entities?.viewerParts ?? {}),
  ),
);

export const getViewerIdsForPartFileUuid = (
  state: RootState,
  partFileUuid: string,
) => {
  const allViewerParts = getAllViewerParts(state);
  return (
    allViewerParts.find(({ partFile }) => partFile === partFileUuid)
      ?.viewerBodyViewerIds || []
  );
};

export const getPartFileCadDataForViewerBody = (
  state: RootState,
  viewerBodyId: string,
) => {
  // gets the partFileCadData slice that contains the requested viewer body
  const cadData = getCadData(state);
  // find the partfile that this body belongs to
  for (const part of Object.values(cadData)) {
    if (part?.entities?.viewerBodies?.[viewerBodyId]) {
      return part;
    }
  }
  return partFileCadDataInitialState;
};

// returns a hash of viewerBodyIds -> viewerBodies for all part files
export const getAllViewerBodiesHash = createSelector(getCadData, (cadData) =>
  Object.fromEntries(
    Object.values(cadData).flatMap((part) =>
      Object.entries(part?.entities?.viewerBodies ?? {}),
    ),
  ),
);

export const getAllPartFiles = createSelector(getCadData, (cadData) =>
  Object.values(cadData).flatMap((part) =>
    Object.values(part?.entities?.partFiles ?? {}),
  ),
);
